import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  forkJoin,
  of,
} from 'rxjs';
import {
  catchError,
  expand,
  map,
  mergeMap,
  reduce,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { ApiService } from '../api/api.service';
import { UserProfileService } from '../user/user-profile.service';
import {
  DeliveryService,
  DeliverySummary,
  NO_DELIVERY,
} from '../message/delivery.service';
import {
  EngagementMessage,
  MessagesReport,
} from '../../pages/organization/campaigns/types/campaignDetails';
import { IMessageReportContacts } from '../../pages/organization/campaigns/types/messagesTypes';

/** Receipt states that mean the SMS will not arrive (matches DeliveryService). */
export const FAILED_DELIVERY_STATES = ['REJECTED', 'UNDELIVERABLE', 'EXPIRED', 'DELETED', 'SKIPPED'];
/** Receipt states that may still turn into a delivery. */
export const IN_FLIGHT_DELIVERY_STATES = ['QUEUED', 'PENDING', 'SCHEDULED', 'EN_ROUTE', 'ACCEPTED', 'UNKNOWN'];

export type DeliveryFilter = 'all' | 'delivered' | 'failed' | 'inflight';

/** Report criteria for one delivery filter. */
export function deliveryCriteria(filter: DeliveryFilter): Record<string, string[]> {
  switch (filter) {
    case 'delivered':
      return { 'deliveryStatus.in': ['DELIVERED'] };
    case 'failed':
      return { 'deliveryStatus.in': FAILED_DELIVERY_STATES };
    case 'inflight':
      return { 'deliveryStatus.in': IN_FLIGHT_DELIVERY_STATES };
    default:
      return {};
  }
}

/** One page of receipt rows, with the total when the API reports one. */
export interface ReceiptPage {
  rows: IMessageReportContacts[];
  total: number | null;
}

/** Where a campaign's failed receipts are read from: by campaign uuid, or by its message uuids. */
export type FailedSource = 'campaign' | 'messages';

/** A page of failed receipts and the source it came from, to reuse for later pages. */
export interface FailedPage extends ReceiptPage {
  source: FailedSource;
}

/**
 * What a campaign message's own GET adds. The campaign details response
 * carries only id, subject, template and content for each message, and
 * CampaignsService copies just the schedules from the per-message GET.
 */
export interface MessageMeta {
  uuid: string | null;
  from: string | null;
  status: string | null;
  sendAt: string | null;
}

const COUNT_URL = 'blackkonnectcxpengagement/api/engagement-message-reports/count';
const REPORTS_URL = 'blackkonnectcxpengagement/api/v1/engagement-message-reports/organization/';
const GENERIC_REPORTS_URL = 'blackkonnectcxpengagement/api/engagement-message-reports';
const CAMPAIGN_REPORTS_URL =
  'blackkonnectcxpengagement/api/v1/engagement-campaign-message-reports/organization/';
const MESSAGE_URL = 'blackkonnectcxpengagement/api/engagement-messages/';

/** Receipts keep arriving after a send, so cached counts expire. */
const CACHE_MS = 60_000;
/** Each outcome is three count requests; cap how many load at once. */
const CONCURRENCY = 4;
/** Rows per request when collecting a whole list for a CSV export. */
const EXPORT_PAGE = 500;
/** Never pull more than this many rows into the browser for one export. */
export const EXPORT_CAP = 20_000;

/**
 * Read-only views of a campaign's delivery receipts for the campaign details
 * pages: how a campaign or one of its sends went (three cheap count queries,
 * like DeliveryService), the per-send report list, and paged receipt rows
 * with their totals so tables can paginate and export.
 *
 * Nothing here touches CampaignsService's shared subjects or loading flags.
 */
@Injectable({ providedIn: 'root' })
export class CampaignInsightsService {
  private api = inject(ApiService);
  private user = inject(UserProfileService);
  private delivery = inject(DeliveryService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private cache = new Map<string, { at: number; result: ReplaySubject<DeliverySummary> }>();
  private queue = new Subject<{
    key: string;
    criteria: Record<string, string>;
    result: ReplaySubject<DeliverySummary>;
  }>();
  private reportCache = new Map<string, { at: number; result: Observable<MessagesReport[]> }>();
  private metaCache = new Map<number, { at: number; result: Observable<MessageMeta | null> }>();

  constructor() {
    this.queue
      .pipe(
        mergeMap(
          ({ key, criteria, result }) =>
            this.loadOutcome(criteria).pipe(
              tap((summary) => {
                result.next(summary);
                result.complete();
              }),
              catchError((error) => {
                // Let the next view retry instead of caching the failure.
                this.cache.delete(key);
                result.error(error);
                return EMPTY;
              })
            ),
          CONCURRENCY
        )
      )
      .subscribe();
  }

  private get organizationId(): string {
    return this.user.organizationId ?? '';
  }

  /** Delivered, failed and in-flight counts for any receipt criteria. */
  outcome(criteria: Record<string, string>): Observable<DeliverySummary> {
    if (!this.isBrowser) return EMPTY;
    const key = JSON.stringify(Object.entries(criteria).sort());

    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.at < CACHE_MS) {
      return cached.result.asObservable();
    }

    const result = new ReplaySubject<DeliverySummary>(1);
    this.cache.set(key, { at: Date.now(), result });
    this.queue.next({ key, criteria, result });
    return result.asObservable();
  }

  /**
   * How a whole campaign went. Counts receipts by campaign uuid; when that
   * finds nothing (older sends may not carry the campaign on each receipt),
   * adds up the per-message outcomes instead, the same numbers the message
   * cards show.
   */
  campaignOutcome(campaignUuid: string, messageUuids: string[]): Observable<DeliverySummary> {
    if (!this.isBrowser || !campaignUuid) return EMPTY;
    const uuids = messageUuids.filter(Boolean);

    const byMessages = (): Observable<DeliverySummary> => {
      if (!uuids.length) return of(NO_DELIVERY);
      return forkJoin(
        uuids.map((uuid) =>
          this.delivery.summary(uuid).pipe(
            take(1),
            catchError(() => of(NO_DELIVERY))
          )
        )
      ).pipe(map(sumOutcomes));
    };

    return this.outcome({ 'campaignUuid.equals': campaignUuid }).pipe(
      take(1),
      catchError(() => of(NO_DELIVERY)),
      switchMap((summary) => (summary.total > 0 ? of(summary) : byMessages()))
    );
  }

  /** How one scheduled send went, by the report's schedule uuid. */
  sendOutcome(scheduleUuid: string): Observable<DeliverySummary> {
    if (!scheduleUuid) return EMPTY;
    return this.outcome({ 'scheduleUuid.equals': scheduleUuid });
  }

  /**
   * Each campaign message with its sends (schedule uuid, time, status,
   * recipients, delivery rate). One request, cached briefly.
   */
  messageReports(campaignUuid: string): Observable<MessagesReport[]> {
    if (!this.isBrowser || !campaignUuid) return EMPTY;

    const cached = this.reportCache.get(campaignUuid);
    if (cached && Date.now() - cached.at < CACHE_MS) return cached.result;

    const result = this.api
      .get<unknown>(`${CAMPAIGN_REPORTS_URL}${this.organizationId}/campaign/${campaignUuid}`)
      .pipe(
        map((data) => (Array.isArray(data) ? (data as MessagesReport[]) : [])),
        catchError((error) => {
          this.reportCache.delete(campaignUuid);
          throw error;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.reportCache.set(campaignUuid, { at: Date.now(), result });
    return result;
  }

  /**
   * One page of a message's receipts with the total from X-Total-Count.
   * Same endpoint and criteria as MessageService.fetchMessageReport, plus
   * page, size and an optional delivery-status filter.
   */
  messageReceipts(messageUuid: string, params: Record<string, unknown>): Observable<ReceiptPage> {
    if (!this.isBrowser || !messageUuid) return EMPTY;
    return this.page(`${REPORTS_URL}${this.organizationId}/message/${messageUuid}`, params);
  }

  /**
   * One page of a campaign's failed receipts. Without a source (the first
   * page), reads them by campaign uuid and, if that comes back empty, by the
   * campaign's message uuids. The result says which source it used; pass it
   * back for later pages and exports so every page reads the same list.
   */
  failedReceipts(
    campaignUuid: string,
    messageUuids: string[],
    page: number,
    size: number,
    source?: FailedSource
  ): Observable<FailedPage> {
    if (!this.isBrowser || !campaignUuid) return EMPTY;
    const failed = deliveryCriteria('failed');
    const uuids = messageUuids.filter(Boolean);

    const byCampaign = (): Observable<FailedPage> =>
      this.receiptsPage({ ...failed, 'campaignUuid.equals': campaignUuid, page, size }).pipe(
        map((result) => ({ ...result, source: 'campaign' as const }))
      );
    const byMessages = (): Observable<FailedPage> =>
      uuids.length
        ? this.receiptsPage({ ...failed, 'messageUuid.in': uuids, page, size }).pipe(
            map((result) => ({ ...result, source: 'messages' as const }))
          )
        : of<FailedPage>({ rows: [], total: 0, source: 'messages' });

    if (source === 'messages') return byMessages();
    if (source === 'campaign') return byCampaign();

    return byCampaign().pipe(
      catchError(() => of<FailedPage>({ rows: [], total: 0, source: 'campaign' })),
      switchMap((result) => (result.rows.length ? of(result) : byMessages()))
    );
  }

  /**
   * Uuid, sender, status and send time of each campaign message, by message
   * id. The delivery views key on the uuid, which the campaign details leave
   * out. One GET per message, cached briefly; a message that fails to load is
   * left out of the map.
   */
  messageMeta(ids: Array<number | null | undefined>): Observable<Record<number, MessageMeta>> {
    const list = [...new Set(ids.filter((id): id is number => id !== null && id !== undefined))];
    if (!this.isBrowser || !list.length) return of({});
    return forkJoin(
      list.map((id) => this.oneMessageMeta(id).pipe(map((meta) => [id, meta] as const)))
    ).pipe(
      map((pairs) => {
        const all: Record<number, MessageMeta> = {};
        for (const [id, meta] of pairs) if (meta) all[id] = meta;
        return all;
      })
    );
  }

  /** Every row a paged reader returns, up to EXPORT_CAP, for a CSV export. */
  collectAll(
    read: (page: number, size: number) => Observable<ReceiptPage>
  ): Observable<IMessageReportContacts[]> {
    const step = (page: number) =>
      read(page, EXPORT_PAGE).pipe(map((result) => ({ page, ...result })));

    return step(0).pipe(
      expand((result) => {
        const fetched = (result.page + 1) * EXPORT_PAGE;
        const more =
          result.rows.length === EXPORT_PAGE &&
          fetched < EXPORT_CAP &&
          (result.total === null || fetched < result.total);
        return more ? step(result.page + 1) : EMPTY;
      }),
      reduce<{ rows: IMessageReportContacts[] }, IMessageReportContacts[]>(
        (all, result) => all.concat(result.rows),
        []
      ),
      map((rows) => rows.slice(0, EXPORT_CAP))
    );
  }

  // ── internals ──

  private oneMessageMeta(id: number): Observable<MessageMeta | null> {
    const cached = this.metaCache.get(id);
    if (cached && Date.now() - cached.at < CACHE_MS) return cached.result;

    const result = this.api.get<Partial<EngagementMessage> | null>(`${MESSAGE_URL}${id}`).pipe(
      map((message) => ({
        uuid: message?.uuid ?? null,
        from: message?.from ?? null,
        status: message?.status ?? null,
        sendAt: message?.sendAt ?? null,
      })),
      catchError(() => {
        // Let the next view retry instead of caching the failure.
        this.metaCache.delete(id);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.metaCache.set(id, { at: Date.now(), result });
    return result;
  }

  private loadOutcome(criteria: Record<string, string>): Observable<DeliverySummary> {
    const count = (states?: string[]) =>
      this.api
        .get<number>(COUNT_URL, {
          ...criteria,
          ...(states ? { 'deliveryStatus.in': states } : {}),
        })
        .pipe(map((value) => Number(value) || 0));

    return forkJoin({
      total: count(),
      delivered: count(['DELIVERED']),
      failed: count(FAILED_DELIVERY_STATES),
    }).pipe(
      map(({ total, delivered, failed }) => ({
        total,
        delivered,
        failed,
        inFlight: Math.max(0, total - delivered - failed),
      }))
    );
  }

  /**
   * Receipts across the organisation filtered by criteria: the
   * organisation-scoped list first, the plain receipts list if that fails.
   */
  private receiptsPage(params: Record<string, unknown>): Observable<ReceiptPage> {
    return this.page(`${REPORTS_URL}${this.organizationId}`, params).pipe(
      catchError(() => this.page(GENERIC_REPORTS_URL, params))
    );
  }

  private page(path: string, params: Record<string, unknown>): Observable<ReceiptPage> {
    return this.api.getDataWithHeaders<unknown>(path, cleanParams(params)).pipe(
      map(({ data, headers }) => {
        const body: any = data;
        const rows: IMessageReportContacts[] = Array.isArray(body)
          ? body
          : Array.isArray(body?.items)
          ? body.items
          : Array.isArray(body?.content)
          ? body.content
          : [];
        const header = headers.get('X-Total-Count');
        const fromHeader = header === null || header === '' ? NaN : Number(header);
        const fromBody = Number(body?.itemPagination?.totalElements ?? body?.totalElements);
        const total = Number.isFinite(fromHeader)
          ? fromHeader
          : Number.isFinite(fromBody)
          ? fromBody
          : null;
        return { rows, total };
      })
    );
  }
}

/** Adds up several delivery outcomes. */
export function sumOutcomes(list: DeliverySummary[]): DeliverySummary {
  return list.reduce<DeliverySummary>(
    (all, one) => ({
      total: all.total + one.total,
      delivered: all.delivered + one.delivered,
      failed: all.failed + one.failed,
      inFlight: all.inFlight + one.inFlight,
    }),
    { ...NO_DELIVERY }
  );
}

/** Drops empty criteria so they never reach the query string. */
function cleanParams(params: Record<string, unknown>): Record<string, any> {
  const clean: Record<string, any> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    clean[key] = Array.isArray(value) ? value.map(String) : String(value);
  }
  return clean;
}
